import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getArticleBySlug, pillarArticles } from '@/mocks/articles';
import { MAIN_SITE_URL } from '@/mocks/categories';
import NotFound from '@/pages/NotFound';

export default function ArticleDetailPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const article = slug ? getArticleBySlug(slug) : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return <NotFound />;
  }

  const related = pillarArticles.filter((a) => a.slug !== article.slug).slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    dateModified: '2026-08-22',
    author: { '@type': 'Organization', name: t('brand') },
    publisher: { '@type': 'Organization', name: t('brand') },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.faq.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <main className="flex-1">
        <div className="bg-cream">
          <div className="mx-auto max-w-3xl px-4 py-10 md:px-6 md:py-14">
            {/* Breadcrumbs */}
            <nav aria-label="Breadcrumb" className="mb-6">
              <ol className="flex flex-wrap items-center gap-2 text-sm text-stone-500">
                <li>
                  <Link to="/" className="transition-colors hover:text-emerald-700">
                    {t('nav.home')}
                  </Link>
                </li>
                <li className="text-stone-300">/</li>
                <li className="text-stone-700">{t('pillar.eyebrow')}</li>
                <li className="text-stone-300">/</li>
                <li className="truncate text-stone-700">{article.title}</li>
              </ol>
            </nav>

            <h1 className="font-display text-2xl font-bold leading-tight text-stone-900 md:text-4xl">
              {article.title}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-stone-500">
              <span className="inline-flex items-center gap-1.5">
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className="ri-time-line text-base"></i>
                </span>
                {article.readingTime}
              </span>
              <span>·</span>
              <span>Last updated: August 2026</span>
            </div>

            <div className="mt-8 overflow-hidden rounded-lg border border-stone-200 bg-white">
              <img
                src={article.image}
                alt={article.title}
                title={article.title}
                className="h-56 w-full object-cover object-top md:h-72"
              />
            </div>

            <p className="mt-8 text-base leading-relaxed text-stone-700 md:text-lg">
              {article.excerpt}
            </p>

            {article.sections.map((section) => (
              <section key={section.heading} className="mt-10">
                <h2 className="font-display text-xl font-bold text-stone-900 md:text-2xl">
                  {section.heading}
                </h2>
                {section.paragraphs.map((p, i) => (
                  <p key={i} className="mt-4 text-base leading-relaxed text-stone-700">
                    {p}
                  </p>
                ))}
              </section>
            ))}

            {/* FAQ */}
            <section className="mt-10">
              <h2 className="font-display text-xl font-bold text-stone-900 md:text-2xl">
                {t('article.faq.h2')}
              </h2>
              <div className="mt-6 space-y-3">
                {article.faq.map((faq, i) => {
                  const isOpen = openFaq === i;
                  return (
                    <div key={faq.q} className="overflow-hidden rounded-lg border border-stone-200 bg-white">
                      <button
                        type="button"
                        onClick={() => setOpenFaq(isOpen ? null : i)}
                        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                      >
                        <span className="text-sm font-semibold text-stone-900 md:text-base">
                          {faq.q}
                        </span>
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center text-stone-500">
                          <i className={`${isOpen ? 'ri-subtract-line' : 'ri-add-line'} text-lg`}></i>
                        </span>
                      </button>
                      {isOpen && (
                        <div className="border-t border-stone-100 px-5 py-4">
                          <p className="text-sm leading-relaxed text-stone-600 md:text-base">{faq.a}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Related */}
            <section className="mt-12 rounded-lg border border-stone-100 bg-white p-6 md:p-8">
              <h2 className="font-display text-xl font-bold text-stone-900 md:text-2xl">
                {t('pillar.title')}
              </h2>
              <div className="mt-5 space-y-2">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/article/${r.slug}`}
                    className="group flex items-center justify-between gap-3 rounded-md border border-stone-100 px-4 py-3 transition-colors hover:border-stone-200 hover:bg-stone-50"
                  >
                    <span className="text-sm font-medium text-stone-800 transition-colors group-hover:text-emerald-700">
                      {r.title}
                    </span>
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center text-stone-400">
                      <i className="ri-arrow-right-line text-base"></i>
                    </span>
                  </Link>
                ))}
              </div>

              <div className="mt-6 flex flex-col gap-3 border-t border-stone-100 pt-6 sm:flex-row">
                <Link
                  to="/"
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-md bg-emerald-600 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  {t('pillar.backHome')}
                </Link>
                <a
                  href={MAIN_SITE_URL}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-stone-200 px-6 py-3 text-sm font-semibold text-stone-800 transition-colors hover:bg-stone-50"
                >
                  {t('finalCta.ctaSecondary')}
                  <i className="ri-arrow-right-up-line ml-2"></i>
                </a>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
    </div>
  );
}